import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { WebSocketProvider } from '../websocket/WebSocketProvider';

const userRoleToSignInRoute = {
    partner: '/partner-sign-in',
    default: '/sign-in'
};

const userOnboardingStatusToRoute = {
    initialised: '/register',
    awaiting_kyc: '/kyc',
    awaiting_approval: '/dashboard',
    awaiting_account_info: '/onboarding',
    awaiting_esign: '/esign',
};

const getSignInRoute = (userRoles = []) => {
    return userRoles.includes('partner') ? userRoleToSignInRoute['partner'] : userRoleToSignInRoute['default'];
};

const ProtectedRoute = ({ element, allowedRoles = [] }) => {
    const { isAuthenticated, errorCode, user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [hasError, setHasError] = useState(false);

    const userRoles = user?.role || [];
    const signInRoute = getSignInRoute(userRoles);
    
    useEffect(() => {
        if (errorCode) {
            switch (errorCode) {
                case 401:
                    setHasError(true);
                    navigate(signInRoute);
                    break;
                case 403:
                    setHasError(true);
                    navigate("/forbidden");
                    break;
                case 500:
                    setHasError(true);
                    navigate("/error");
                    break;
                default:
                    break;
            }
        }
    }, [errorCode, navigate, signInRoute]);

    if (hasError) {
        return null; // Optionally show a loading state while redirecting
    }

    if (!isAuthenticated) {
        return <Navigate to={signInRoute} replace />;
    }

    const onboardingStatus = user?.user_profile?.onboarding_status;
    if (onboardingStatus === 'suspended') {
        logout();
        return 'Your account has been suspended';
    }

    if (onboardingStatus in userOnboardingStatusToRoute) {
        const route = userOnboardingStatusToRoute[onboardingStatus];
        if (route !== location.pathname) {
            return <Navigate to={route} replace />;
        }
    }

    if (Object.values(userOnboardingStatusToRoute).includes(location.pathname) &&
        onboardingStatus === 'approved' &&
        location.pathname !== '/dashboard') {
        return <Navigate to={'/dashboard'} replace />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.some((role) => userRoles.includes(role))) {
        return <Navigate to={'/dashboard'} replace />;
    }

    // ✅ Wrap the protected component with WebSocketProvider
    return userRoles.includes('support') ? (
        <WebSocketProvider userId={user.id}>
            {element}
        </WebSocketProvider>
    ) : (
        element
    );
};

export default ProtectedRoute;
